import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PanToolIcon from '@mui/icons-material/PanTool';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { getSocket, emitAsync } from '../../services/socket';

export default function ParticipantsPanel({
  participants,
  localParticipant,
  isHost,
  waitingList,
  onClose,
}) {
  const handleAdmit = async (guestSocketId) => {
    try {
      await emitAsync('admit-participant', { guestSocketId });
    } catch (err) {
      console.error('Failed to admit participant:', err);
    }
  };

  const handleReject = async (guestSocketId) => {
    try {
      await emitAsync('reject-participant', { guestSocketId });
    } catch (err) {
      console.error('Failed to reject participant:', err);
    }
  };

  const handleMute = (targetSocketId) => {
    getSocket()?.emit('mute-participant', { targetSocketId });
  };

  const handleKick = async (targetSocketId) => {
    try {
      await emitAsync('kick-participant', { targetSocketId });
    } catch (err) {
      console.error('Failed to kick participant:', err);
    }
  };

  const allParticipants = [
    { ...localParticipant, isLocal: true },
    ...participants,
  ];

  return (
    <Box
      sx={{
        width: 320,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <Typography variant="h6">Participants ({allParticipants.length})</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Waiting room */}
      {isHost && waitingList.length > 0 && (
        <>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
              Waiting Room ({waitingList.length})
            </Typography>
            <List dense disablePadding>
              {waitingList.map((guest) => (
                <ListItem key={guest.socketId} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                      {guest.displayName?.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={guest.displayName} />
                  <ListItemSecondaryAction>
                    <Tooltip title="Admit">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleAdmit(guest.socketId)}
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleReject(guest.socketId)}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
          <Divider />
        </>
      )}

      {/* Participants list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List>
          {allParticipants.map((participant) => (
            <ListItem
              key={participant.isLocal ? 'local' : participant.socketId}
              sx={{
                '&:hover .actions': { opacity: 1 },
              }}
            >
              <ListItemAvatar>
                <Avatar>
                  {participant.displayName?.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {participant.displayName}
                    {participant.isLocal && (
                      <Chip label="You" size="small" sx={{ height: 20 }} />
                    )}
                    {participant.isHost && (
                      <Chip label="Host" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    {participant.audioEnabled ? (
                      <MicIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <MicOffIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    {participant.videoEnabled ? (
                      <VideocamIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <VideocamOffIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    {participant.handRaised && (
                      <PanToolIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    )}
                  </Box>
                }
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
              {isHost && !participant.isLocal && (
                <ListItemSecondaryAction
                  className="actions"
                  sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                >
                  <Tooltip title="Mute">
                    <IconButton
                      size="small"
                      onClick={() => handleMute(participant.socketId)}
                      disabled={!participant.audioEnabled}
                    >
                      <MicOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleKick(participant.socketId)}
                    >
                      <PersonRemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}
